/**
 * Web Crypto API 기반 암호화 서비스
 * 
 * 주요 특징:
 * - 브라우저 네이티브 Web Crypto API 사용
 * - AES-256-GCM 암호화 (인증 태그 포함)
 * - PBKDF2 키 파생 (NIST SP 800-132)
 * - 완전한 async/await 패턴
 * - CryptoJS 하위 호환성 지원
 */

import { getUserEncryptionSalt, createUserKeyString } from './userKeyService';

/**
 * 암호화 설정 상수 (Web Crypto API 기준)
 */
const WEB_CRYPTO_CONFIG = {
  // AES-256-GCM 설정
  algorithm: 'AES-GCM',
  keyLength: 256,         // 256비트 키
  ivLength: 12,           // 96비트 IV (GCM 권장)
  tagLength: 16,          // 128비트 인증 태그
  
  // PBKDF2 설정
  saltLength: 16,         // 128비트 Salt
  iterations: 100000,     // 2024년 권장값
  
  // 메타데이터
  version: '2.0',  // v2.0: 사용자별 고유 salt 추가
  standard: 'WebCrypto-2025'
};

/**
 * 안전한 랜덤 바이트 생성 (Web Crypto API)
 * @param {number} length - 바이트 길이
 * @returns {Uint8Array} 랜덤 바이트 배열
 */
export function generateSecureRandom(length) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return array;
}

/**
 * ArrayBuffer를 Base64 문자열로 변환
 * @param {ArrayBuffer} buffer - 변환할 버퍼
 * @returns {string} Base64 문자열
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Base64 문자열을 ArrayBuffer로 변환
 * @param {string} base64 - Base64 문자열
 * @returns {ArrayBuffer} 변환된 버퍼
 */
function base64ToArrayBuffer(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * PBKDF2를 사용한 키 파생 (Web Crypto API)
 * @param {string} password - 원본 패스워드 (사용자 UID)
 * @param {Uint8Array} salt - Salt 바이트
 * @param {number} iterations - 반복 횟수
 * @returns {Promise<CryptoKey>} 파생된 암호화 키
 */
async function deriveKey(password, salt, iterations = WEB_CRYPTO_CONFIG.iterations) {
  // 패스워드를 키 재료로 변환
  const passwordBuffer = new TextEncoder().encode(password);
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );
  
  // PBKDF2로 AES 키 파생
  return await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: iterations,
      hash: 'SHA-256'
    },
    keyMaterial,
    {
      name: 'AES-GCM',
      length: WEB_CRYPTO_CONFIG.keyLength
    },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * AES-256-GCM 암호화 (Web Crypto API)
 * @param {string} plaintext - 암호화할 평문
 * @param {string} userUid - 사용자 UID (키 파생용)
 * @returns {Promise<Object>} 암호화 결과 객체
 */
export async function encryptData(plaintext, userUid) {
  if (!plaintext || !userUid) {
    throw new Error('plaintext와 userUid가 필요합니다');
  }

  try {
    // 1. 각 암호화마다 새로운 salt와 IV 생성
    const salt = generateSecureRandom(WEB_CRYPTO_CONFIG.saltLength);
    const iv = generateSecureRandom(WEB_CRYPTO_CONFIG.ivLength);
    
    // 2. 키 파생 - 사용자 salt 시도, 실패 시 레거시 방식
    let key;
    let version = WEB_CRYPTO_CONFIG.version;
    
    try {
      // 사용자별 고유 salt 가져오기 시도
      const userSalt = await getUserEncryptionSalt(userUid);
      const keyString = createUserKeyString(userUid, userSalt);
      key = await deriveKey(keyString, salt);
      version = '2.0'; // 새로운 버전
    } catch (error) {
      // 사용자 salt 가져오기 실패 시 레거시 방식 사용
      if (process.env.NODE_ENV === 'development') {
        console.debug('📱 Using legacy encryption (common on mobile)');
      }
      key = await deriveKey(userUid, salt);
      version = '1.0'; // 레거시 버전
    }
    
    // 3. 평문을 바이트로 변환
    const plaintextBuffer = new TextEncoder().encode(plaintext);
    
    // 4. AES-256-GCM 암호화
    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        tagLength: WEB_CRYPTO_CONFIG.tagLength * 8 // 비트 단위
      },
      key,
      plaintextBuffer
    );

    // 5. 결과 반환
    return {
      version: version, // 실제 사용된 버전
      standard: WEB_CRYPTO_CONFIG.standard,
      algorithm: WEB_CRYPTO_CONFIG.algorithm,
      ciphertext: arrayBufferToBase64(encryptedBuffer),
      iv: arrayBufferToBase64(iv),
      salt: arrayBufferToBase64(salt),
      iterations: WEB_CRYPTO_CONFIG.iterations,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Web Crypto 암호화 실패:', error);
    throw new Error(`암호화 실패: ${error.message}`);
  }
}

/**
 * AES-256-GCM 복호화 (Web Crypto API)
 * @param {Object} encryptedData - 암호화 데이터 객체
 * @param {string} userUid - 사용자 UID (키 파생용)
 * @returns {Promise<string>} 복호화된 평문
 */
export async function decryptData(encryptedData, userUid) {
  if (!encryptedData || !userUid) {
    throw new Error('encryptedData와 userUid가 필요합니다');
  }

  try {
    // 1. 메타데이터 검증
    if (!encryptedData.version || !encryptedData.ciphertext) {
      throw new Error('유효하지 않은 암호화 데이터 형식');
    }

    // 2. Base64 디코딩
    const ciphertextBuffer = base64ToArrayBuffer(encryptedData.ciphertext);
    const iv = new Uint8Array(base64ToArrayBuffer(encryptedData.iv));
    const salt = new Uint8Array(base64ToArrayBuffer(encryptedData.salt));
    
    // 3. 키 파생 - 버전에 따라 다르게 처리
    let key;
    if (encryptedData.version === '2.0') {
      // 새로운 버전: 사용자별 고유 salt 사용
      try {
        const userSalt = await getUserEncryptionSalt(userUid);
        const keyString = createUserKeyString(userUid, userSalt);
        key = await deriveKey(keyString, salt, encryptedData.iterations);
      } catch (error) {
        // 사용자 salt 가져오기 실패 시 레거시 방식으로 fallback
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ 향상된 보안 기능 사용 불가, 레거시 방식으로 fallback');
        }
        key = await deriveKey(userUid, salt, encryptedData.iterations);
      }
    } else {
      // 레거시 버전: userUid만 사용
      key = await deriveKey(userUid, salt, encryptedData.iterations);
    }
    
    // 4. AES-256-GCM 복호화
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        tagLength: WEB_CRYPTO_CONFIG.tagLength * 8
      },
      key,
      ciphertextBuffer
    );

    // 5. 바이트를 문자열로 변환
    const plaintext = new TextDecoder().decode(decryptedBuffer);
    
    if (!plaintext) {
      throw new Error('복호화 결과가 비어있습니다');
    }

    return plaintext;
  } catch (error) {
    console.error('Web Crypto 복호화 실패:', error);
    throw new Error(`복호화 실패: ${error.message}`);
  }
}

/**
 * 레거시 CryptoJS 데이터 복호화 (하위 호환성)
 * @param {string} legacyEncrypted - CryptoJS로 암호화된 데이터
 * @param {string} userUid - 사용자 UID
 * @returns {Promise<string>} 복호화된 평문
 */
export async function decryptLegacyData(legacyEncrypted, userUid) {
  // 이 함수는 임시로 CryptoJS 없이는 구현할 수 없으므로
  // 에러를 발생시켜 마이그레이션을 유도
  throw new Error('레거시 데이터 복호화를 위해서는 수동 마이그레이션이 필요합니다');
}

/**
 * 세션 전용 암호화 (사용자 salt 없이 고정 키 사용)
 * @param {string} plaintext - 암호화할 평문
 * @param {string} fixedKey - 고정 키 문자열
 * @returns {Promise<string>} 암호화된 데이터 (JSON 문자열)
 */
export async function encryptSession(plaintext, fixedKey) {
  if (!plaintext || !fixedKey) {
    throw new Error('plaintext와 fixedKey가 필요합니다');
  }

  try {
    // 1. 각 암호화마다 새로운 salt와 IV 생성
    const salt = generateSecureRandom(WEB_CRYPTO_CONFIG.saltLength);
    const iv = generateSecureRandom(WEB_CRYPTO_CONFIG.ivLength);
    
    // 2. 고정 키로 직접 키 파생 (사용자 salt 시스템 사용 안 함)
    const key = await deriveKey(fixedKey, salt);
    
    // 3. 평문을 바이트로 변환
    const plaintextBuffer = new TextEncoder().encode(plaintext);
    
    // 4. AES-256-GCM 암호화
    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        tagLength: WEB_CRYPTO_CONFIG.tagLength * 8
      },
      key,
      plaintextBuffer
    );

    // 5. 결과 반환
    const result = {
      version: '1.0', // 세션용 버전
      standard: WEB_CRYPTO_CONFIG.standard,
      algorithm: WEB_CRYPTO_CONFIG.algorithm,
      ciphertext: arrayBufferToBase64(encryptedBuffer),
      iv: arrayBufferToBase64(iv),
      salt: arrayBufferToBase64(salt),
      iterations: WEB_CRYPTO_CONFIG.iterations,
      timestamp: new Date().toISOString()
    };
    
    return JSON.stringify(result);
  } catch (error) {
    console.error('세션 암호화 실패:', error);
    throw new Error(`세션 암호화 실패: ${error.message}`);
  }
}

/**
 * 세션 전용 복호화
 * @param {string} encryptedText - 암호화된 데이터 (JSON 문자열)
 * @param {string} fixedKey - 고정 키 문자열
 * @returns {Promise<string>} 복호화된 평문
 */
export async function decryptSession(encryptedText, fixedKey) {
  if (!encryptedText || !fixedKey) {
    throw new Error('encryptedText와 fixedKey가 필요합니다');
  }

  try {
    // 1. JSON 파싱
    const encryptedData = JSON.parse(encryptedText);
    
    // 2. Base64 디코딩
    const ciphertextBuffer = base64ToArrayBuffer(encryptedData.ciphertext);
    const iv = new Uint8Array(base64ToArrayBuffer(encryptedData.iv));
    const salt = new Uint8Array(base64ToArrayBuffer(encryptedData.salt));
    
    // 3. 고정 키로 키 파생
    const key = await deriveKey(fixedKey, salt, encryptedData.iterations);
    
    // 4. AES-256-GCM 복호화
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        tagLength: WEB_CRYPTO_CONFIG.tagLength * 8
      },
      key,
      ciphertextBuffer
    );

    // 5. 바이트를 문자열로 변환
    const plaintext = new TextDecoder().decode(decryptedBuffer);
    
    if (!plaintext) {
      throw new Error('복호화 결과가 비어있습니다');
    }

    return plaintext;
  } catch (error) {
    console.error('세션 복호화 실패:', error);
    throw new Error(`세션 복호화 실패: ${error.message}`);
  }
}

/**
 * 기존 API와의 호환성을 위한 래퍼 함수들
 */

export const encrypt = async (text, userUid) => {
  const result = await encryptData(text, userUid);
  return JSON.stringify(result);
};

export const decrypt = async (encryptedText, userUid) => {
  try {
    // JSON 형식인지 확인 (새로운 Web Crypto 형식)
    const encryptedData = JSON.parse(encryptedText);
    
    // 버전 체크 - 구버전인 경우 레거시 복호화 시도
    if (encryptedData.version === '1.0' || !encryptedData.version) {
      try {
        // 레거시 키 파생 (userUid만 사용)
        const salt = new Uint8Array(base64ToArrayBuffer(encryptedData.salt));
        const key = await deriveKey(userUid, salt, encryptedData.iterations);
        
        const ciphertextBuffer = base64ToArrayBuffer(encryptedData.ciphertext);
        const iv = new Uint8Array(base64ToArrayBuffer(encryptedData.iv));
        
        const decryptedBuffer = await crypto.subtle.decrypt(
          {
            name: 'AES-GCM',
            iv: iv,
            tagLength: WEB_CRYPTO_CONFIG.tagLength * 8
          },
          key,
          ciphertextBuffer
        );
        
        return new TextDecoder().decode(decryptedBuffer);
      } catch (legacyError) {
        // 레거시 복호화 실패 시 새로운 방식 시도
        return await decryptData(encryptedData, userUid);
      }
    }
    
    return await decryptData(encryptedData, userUid);
  } catch (parseError) {
    // JSON 파싱 실패시 레거시 데이터로 간주
    if (process.env.NODE_ENV === 'development') {
      console.warn('⚠️ 레거시 암호화 데이터 감지');
    }
    throw new Error('레거시 데이터는 수동 마이그레이션이 필요합니다. 개발서버에서 데이터를 다시 저장해주세요.');
  }
};

/**
 * Web Crypto 서비스 정보
 */
export const WebCryptoInfo = {
  version: WEB_CRYPTO_CONFIG.version,
  standard: WEB_CRYPTO_CONFIG.standard,
  algorithm: WEB_CRYPTO_CONFIG.algorithm,
  keyLength: WEB_CRYPTO_CONFIG.keyLength,
  features: [
    'AES-256-GCM 암호화 (인증 태그 포함)',
    'PBKDF2 키 파생 (SHA-256)',
    '96비트 IV + 128비트 인증 태그',
    '브라우저 네이티브 Web Crypto API',
    '완전한 async/await 지원',
    '향상된 보안성과 성능'
  ]
};

/**
 * 개발/테스트용 함수들
 */
export const webCryptoDevTools = {
  /**
   * 암호화 테스트
   */
  runTest: async () => {
    try {
      const testText = 'Hello Web Crypto API!';
      const testUid = 'test-user-123';
      
      console.log('🧪 Web Crypto 테스트 시작');
      
      const encrypted = await encrypt(testText, testUid);
      console.log('✅ 암호화 성공:', encrypted.substring(0, 100) + '...');
      
      const decrypted = await decrypt(encrypted, testUid);
      console.log('✅ 복호화 성공:', decrypted);
      
      const success = decrypted === testText;
      console.log(success ? '🎉 테스트 성공!' : '❌ 테스트 실패!');
      
      return { success, encrypted, decrypted };
    } catch (error) {
      console.error('❌ 테스트 실패:', error);
      return { success: false, error: error.message };
    }
  },
  
  /**
   * 성능 벤치마크
   */
  benchmark: async (iterations = 100) => {
    const testText = 'Performance test data';
    const testUid = 'benchmark-user';
    
    console.log(`🏃 성능 테스트 시작 (${iterations}회)`);
    
    const start = performance.now();
    
    for (let i = 0; i < iterations; i++) {
      const encrypted = await encrypt(testText, testUid);
      await decrypt(encrypted, testUid);
    }
    
    const end = performance.now();
    const totalTime = end - start;
    
    return {
      iterations,
      totalTime: totalTime.toFixed(2) + 'ms',
      avgTime: (totalTime / iterations).toFixed(2) + 'ms',
      opsPerSecond: Math.round(1000 / (totalTime / iterations))
    };
  }
};
